/**
 * Net Impact drill-in side panel (PLT-4915).
 *
 * Shell + tab strip. The four tabs (Positive, Negative, Everyday units,
 * Chat) are dumb leaf components in `./tabs/`; this file owns the
 * chrome and the cross-tab state (which tab is active).
 *
 * The panel positions itself in the same right-edge slot as the
 * `ChatSidePanel` (same `position: fixed` + `top/right/bottom` offsets)
 * so the chat-tab "send" hand-off is visually a content change rather
 * than a layout reshuffle — see `chat.tsx` for the close-then-open
 * choreography.
 *
 * Self-gated: subscribes to the module-level state in `./state.ts` via
 * `useSyncExternalStore` and renders nothing when the panel is closed.
 * Safe to mount unconditionally at the app root.
 *
 * Design notes (PLT-4915 Janne review v3, May 2026):
 *   - Header — no breadcrumb, no divider line below; title (24px) sits
 *     directly above a short category definition line (gray-700, 13px)
 *     and the POSITIVE / NEGATIVE / NET stat row. The definition is
 *     `uiImpactCategoryByCode[code].explanations.productViewExplanation`
 *     — the same one-liner the legacy impact-explanation modal uses to
 *     describe the category. Janne asked us to bring it back because
 *     opening the panel from a chart was previously the user's only
 *     "what does this category cover?" surface, and dropping the
 *     breadcrumb removed the implicit answer.
 *   - Stat row — three equal-width columns, no inter-column dividers,
 *     POSITIVE / NEGATIVE / NET at xl bold. Same layout the mock images
 *     have; we restored the size in review v2 and the gap-only spacing
 *     in v3.
 *   - Tab strip — four equal-width tabs (Positive, Negative, Everyday
 *     units, Chat). The first two replace the v2 single "Contributing
 *     products" tab so clicking a positive bar lands on the positive
 *     contributors list directly, and a click on the negative bar lands
 *     on the negative one — no extra click to flip valence. "Everyday
 *     units" is the v3 name for the old "Real-world units" tab.
 *   - All colour + spacing comes from `--color__*` / `--space__*`
 *     tokens. The tab strip uses no chartreuse accent — DESIGN.md
 *     restricts `#E0FF33` to the two sanctioned chat surfaces (send
 *     button + welcome-state pills).
 */
import { ModelEntityType } from '@upright/common/model/input/types';
import { ExampleEntityIds } from '@upright/common/model/output/scores/hard-coded-entity-ids';
import { Valence } from '@upright/common/model/types';
import { DeleteSteamlineCore } from '@upright/ui/icons';
import {
  ButtonMenuContainer,
  ButtonMenuTabContent,
  ButtonMenuTabList,
  ButtonMenuTabListItem,
} from '@upright/ui/common/generic/components/tab-switches/button-menu';
import { breakpoints } from '@upright/ui/common/generic/global/breakpoints';
import { useOnClickOutside, useViewSetting } from '@upright/ui/common/generic/hooks';
import { useIsLeena2ResearchPreview } from '@upright/ui/common/upright/model/output/leena2-research-preview';
import { Leena2TopImpacts } from '@upright/ui/views/model-entity-views/company-view/drilldown/leena2-top-impacts';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import styled from 'styled-components';
import { AboutTab } from './tabs/about';
import { ContributorsTab } from './tabs/contributors';
import { EverydayUnitsTab } from './tabs/everyday-units';
import { ChatTab, useNetImpactChatTabMode } from './tabs/chat';
import { ProductValenceTab } from './tabs/product-valence';
import {
  closeNetImpactDrillIn,
  drillInDefinition,
  drillInTitle,
  getNetImpactDrillInState,
  isDimensionTarget,
  NetImpactDrillInPayload,
  NetImpactDrillInTab,
  subscribeNetImpactDrillInState,
} from './state';

export function NetImpactDrillInPanel() {
  const state = useSyncExternalStore(subscribeNetImpactDrillInState, getNetImpactDrillInState);
  if (!state.open || state.payload === null) return null;
  // `nonce` as the React key forces a clean unmount/mount cycle on every
  // open call so the inner body never inherits stale per-payload state
  // (active tab, composer draft, etc.) from a previous open. Mirrors
  // `chat-panel-state.ts`'s `seedNonce` mechanism.
  return <NetImpactDrillInPanelOpen key={state.nonce} payload={state.payload} initialTab={state.initialTab} />;
}

function NetImpactDrillInPanelOpen({
  payload,
  initialTab,
}: {
  payload: NetImpactDrillInPayload;
  initialTab: NetImpactDrillInTab;
}) {
  // Esc closes the panel — same convention as the chat side panel
  // (`chat-side-panel.tsx`).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        closeNetImpactDrillIn();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  // Click-outside-to-dismiss (Janne review, Aug 2026), pairing with the Esc
  // handler above — the standard drawer/overlay dismissal pattern. Clicks
  // inside the panel (tabs, links, the close button) are ignored because
  // they land within `panelRef`. The listener is attached in an effect after
  // mount, so the chart click that opened the panel has already finished
  // dispatching and can't immediately re-close it.
  const panelRef = useRef<HTMLDivElement>(null);
  useOnClickOutside([panelRef], closeNetImpactDrillIn);

  // `impactLabel` is the panel title — the friendly impact-category name
  // at impact grain, or the dimension name (e.g. "Environment") at
  // dimension grain. Kept named `impactLabel` because the tab components
  // take an `impactLabel` prop they weave into their section headings.
  const impactLabel = drillInTitle(payload.target);
  const entityName = payload.entity.name ?? 'this entity';
  const categoryDefinition = drillInDefinition(payload.target);
  // Dimensions aggregate ~5 impact categories whose physical "everyday"
  // units (DALYs, GHG tons, jobs…) can't be summed into one figure, so
  // the units tab is hidden at dimension grain (PLT-4940).
  const isDimension = isDimensionTarget(payload.target);
  // PLT-5427: the Chat tab is dropped from the strip (not just blanked)
  // for logged-in users in a provisioned org that lacks `USE_AI_CHAT_WEB`
  // — same way `/chat` and the "Ask AI" entry points disappear for them.
  // `composer` / `upsell` keep the tab; only `hidden` removes it. The
  // tab body reads the same hook, so the strip and body never disagree.
  const chatTabMode = useNetImpactChatTabMode();
  // PLT-7045: opened from a product, the valence tabs hold the product's own detail rather than
  // contributing products, and the company-shaped tabs drop out: Everyday units converts dollars a
  // year, which a product has no revenue to give, and the chat hand-off binds a company.
  const isProduct = payload.entity.type === ModelEntityType.PRODUCT;
  const showChatTab = chatTabMode !== 'hidden' && !isProduct;
  const showUnitsTab = !isDimension && !isProduct;
  // Guard the initial selection so `defaultSelectedKey` never points at a
  // tab that isn't rendered:
  //   - `chat` seeded for a viewer whose Chat tab is hidden, and
  //   - `about` seeded at dimension grain (the About tab, like Everyday
  //     units, only renders at impact grain — a dimension has no single
  //     explanation). A dimension-row label click therefore falls back to
  //     Positive.
  // Both fall back to the always-present Positive tab.
  const effectiveInitialTab =
    (initialTab === 'chat' && !showChatTab) ||
    (initialTab === 'about' && isDimension) ||
    (initialTab === 'units' && !showUnitsTab)
      ? 'positive'
      : initialTab;
  // The valence side the user chose, which the Chat tab's "why" suggestion
  // follows (PLT-7788): seeded from the bar they clicked, then updated each
  // time they select the Positive or Negative tab. Seeded from `initialTab`
  // rather than `effectiveInitialTab` so the Positive fallback above, which
  // the user never chose, doesn't count as a choice.
  const [selectedValence, setSelectedValence] = useState<'positive' | 'negative' | null>(
    initialTab === 'positive' || initialTab === 'negative' ? initialTab : null,
  );
  // Team-only + disabled-by-default Leena-2 "Top impacts" list, shown below the
  // contributing-products list on each valence tab. `useViewSetting` already applies
  // the team gate; `Leena2TopImpacts` self-hides on loading/error/empty. It reads its
  // own v2.0.0 metrics, so it's a sibling of (not nested in) ContributorsTab — the
  // contributors query can 410 on a deleted variant version while this still renders.
  // Leena-2 metrics exist for most products, so the list would show on every release;
  // it is shown only on the Leena-2 research preview, except for Tesla, which keeps it
  // on every version (PLT-7064).
  const isLeena2ResearchPreview = useIsLeena2ResearchPreview();
  const showTopImpacts =
    useViewSetting('companyProduct.leena2TopImpactsInDrilldown') &&
    (isLeena2ResearchPreview || payload.entity.id === (ExampleEntityIds.TESLA as string));
  // Leena-2 "Top impacts" takes a single `impactCode`; the dimension grain has no
  // single code (PLT-4940), so the list only renders at impact grain. `undefined`
  // here short-circuits the render in both valence tabs below.
  const topImpactsCode = payload.target.grain === 'impact' ? payload.target.impactCode : undefined;

  // Net = pos - neg (the chart already displays them this way — see
  // `total-sums-area.tsx`; positives subtract magnitudes for negatives).
  const netCentsPerDollar = computeNet(payload.positiveCentsPerDollar, payload.negativeCentsPerDollar);

  return (
    <Root ref={panelRef} role="complementary" aria-label={`${impactLabel} drill-in`}>
      <Header>
        <HeaderTopRow>
          <Title>{impactLabel}</Title>
          <CloseButton type="button" onClick={closeNetImpactDrillIn} aria-label="Close drill-in panel">
            <DeleteSteamlineCore width={14} height={14} />
          </CloseButton>
        </HeaderTopRow>
        {categoryDefinition && <Definition>{categoryDefinition}</Definition>}
        <StatRow>
          <Stat>
            <StatLabel>POSITIVE</StatLabel>
            <StatValue $valence={Valence.POSITIVE}>
              {formatCentsPerDollar(payload.positiveCentsPerDollar, '+')}
            </StatValue>
          </Stat>
          <Stat>
            <StatLabel>NEGATIVE</StatLabel>
            <StatValue $valence={Valence.NEGATIVE}>
              {formatCentsPerDollar(payload.negativeCentsPerDollar, '−')}
            </StatValue>
          </Stat>
          <Stat>
            <StatLabel>NET</StatLabel>
            <StatValue $valence={Valence.OTHER}>{formatNet(netCentsPerDollar)}</StatValue>
          </Stat>
        </StatRow>
      </Header>

      <TabsWrap>
        <ButtonMenuContainer
          defaultSelectedKey={effectiveInitialTab}
          onSelectionChange={key => {
            if (key === 'positive' || key === 'negative') setSelectedValence(key);
          }}
        >
          <TabStrip>
            <ButtonMenuTabList ariaLabel="Drill-in tabs" variant="pill" fontSize="xs" tabPadding="6px var(--space__sm)">
              {!isDimension && <ButtonMenuTabListItem id="about">About</ButtonMenuTabListItem>}
              <ButtonMenuTabListItem id="positive">Positive</ButtonMenuTabListItem>
              <ButtonMenuTabListItem id="negative">Negative</ButtonMenuTabListItem>
              {showUnitsTab && <ButtonMenuTabListItem id="units">Everyday units</ButtonMenuTabListItem>}
              {showChatTab && <ButtonMenuTabListItem id="chat">Chat</ButtonMenuTabListItem>}
            </ButtonMenuTabList>
          </TabStrip>
          <ScrollArea>
            {!isDimension && (
              <ButtonMenuTabContent id="about">
                <AboutTab payload={payload} impactLabel={impactLabel} entityName={entityName} />
              </ButtonMenuTabContent>
            )}
            {([Valence.POSITIVE, Valence.NEGATIVE] as const).map(valence => (
              <ButtonMenuTabContent key={valence} id={valence === Valence.POSITIVE ? 'positive' : 'negative'}>
                {isProduct ? (
                  <ProductValenceTab
                    payload={payload}
                    valence={valence}
                    impactLabel={impactLabel}
                    entityName={entityName}
                  />
                ) : (
                  <ValenceTabBody>
                    <ContributorsTab
                      payload={payload}
                      valence={valence}
                      impactLabel={impactLabel}
                      entityName={entityName}
                      // The same audience as the Chat tab's composer.
                      showAskWhy={chatTabMode === 'composer'}
                    />
                    {showTopImpacts && topImpactsCode && (
                      <Leena2TopImpacts
                        source={{ kind: 'company', resultId: payload.modelResultId }}
                        impactCode={topImpactsCode}
                        valence={valence}
                        showValueChains={payload.showValueChains}
                      />
                    )}
                  </ValenceTabBody>
                )}
              </ButtonMenuTabContent>
            ))}
            {showUnitsTab && (
              <ButtonMenuTabContent id="units">
                <EverydayUnitsTab payload={payload} impactLabel={impactLabel} entityName={entityName} />
              </ButtonMenuTabContent>
            )}
            {showChatTab && (
              <ButtonMenuTabContent id="chat">
                <ChatTab
                  payload={payload}
                  impactLabel={impactLabel}
                  entityName={entityName}
                  selectedValence={selectedValence}
                />
              </ButtonMenuTabContent>
            )}
          </ScrollArea>
        </ButtonMenuContainer>
      </TabsWrap>
    </Root>
  );
}

function computeNet(positive: number | null, negative: number | null): number | null {
  if (positive === null && negative === null) return null;
  return (positive ?? 0) - (negative ?? 0);
}

function formatCentsPerDollar(value: number | null, sign: '+' | '−'): string {
  if (value === null || Number.isNaN(value)) return '—';
  // Display in cents-per-dollar (c/$). The `value` is already in
  // cents-per-dollar units (set by the click handler). We `Math.abs`
  // before precision-formatting so a stray negative magnitude can't
  // produce "+-3.0 c/$" — the caller owns the sign. Mirrors `formatNet`
  // below.
  const abs = Math.abs(value);
  const rounded = abs < 1 ? abs.toPrecision(1) : abs.toPrecision(3);
  return `${sign}${rounded} c/$`;
}

function formatNet(value: number | null): string {
  if (value === null || Number.isNaN(value)) return '—';
  const sign = value >= 0 ? '+' : '−';
  const abs = Math.abs(value);
  const rounded = abs < 1 ? abs.toPrecision(1) : abs.toPrecision(3);
  return `${sign}${rounded} c/$`;
}

/* ─── Styled components ──────────────────────────────────────────────── */

const Root = styled.div`
  position: fixed;
  /* Same right-edge slot as \`.chat-side-panel\` (see \`upright-chat-
     theme.css\`). Identical offsets so the chat-tab handoff is visually
     a content change. */
  top: calc(48px + var(--space__lg));
  right: var(--space__lg);
  /* Bottom gap is a literal \`--space__lg\` so the panel-to-viewport-bottom
     distance matches the right-to-viewport distance to the pixel (PLT-4935,
     Janne review). Previously this was \`calc(25px + var(--space__lg) * 2)\` —
     the doubled gap plus a 25px status-bar offset — which read as extra
     dead space below the panel. The 25px page-label status bar still renders
     behind the panel's bottom strip and stays fully visible to its left.
     Same resolution as the chat side panel in PLT-4932. */
  bottom: var(--space__lg);
  width: clamp(440px, 36vw, 520px);
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: var(--border-radius__xl);
  border: 1px solid var(--color__gray-200);
  box-shadow:
    0 12px 32px rgba(31, 35, 40, 0.12),
    0 2px 6px rgba(31, 35, 40, 0.06);
  /* Same z-index as the chat panel — they swap in the same slot. */
  z-index: 1100;
  overflow: hidden;
  animation: ni-drill-in-slide-in 180ms ease-out;

  /* PLT-6096: below \`md\` the 440px floor above is wider than a phone viewport,
     so the panel overflowed the screen edge with no breakpoint at all. It now
     takes the viewport minus a small inset — same treatment and same inset as
     \`.chat-side-panel\`, which shares this slot, so it stays a non-modal panel
     rather than becoming a bottom sheet and the tab handoff is still just a
     content change. The top offset keeps the 48px header allowance because the
     panel renders on pages that show the header at every width. */
  @media (max-width: ${breakpoints[1]}) {
    top: calc(48px + var(--space__sm));
    right: var(--space__sm);
    bottom: var(--space__sm);
    left: var(--space__sm);
    width: auto;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @keyframes ni-drill-in-slide-in {
    from {
      transform: translateX(16px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const Header = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space__md);
  padding: var(--space__xl) var(--space__xl) var(--space__sm);
  /* No divider between header and tab strip — the gap below the stat
     row + the space above the tabs already separate the two. */
`;

const HeaderTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--space__sm);
`;

const Title = styled.div`
  flex: 1;
  min-width: 0;
  font-size: var(--font-size__2xl);
  font-weight: var(--font-weight__bold);
  color: var(--color__gray-900);
  line-height: 1.2;
  /* Let long names like "Scarce natural resources" wrap rather than
     ellipsize — the title is the anchor of the panel. \`text-wrap:
     balance\` evenly distributes words across the wrapped lines, so a
     three-word title like "Scarce natural resources" doesn't break
     after the first word. */
  word-break: break-word;
  text-wrap: balance;
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--border-radius__sm);
  cursor: pointer;
  color: var(--color__gray-600);
  &:hover {
    background: var(--color__gray-100);
    color: var(--color__gray-900);
  }
`;

/* Definition under the title — single short line describing what the
   impact category covers. Sourced from `productViewExplanation`, the
   same string the legacy `ImpactExplanationModal` uses. Sits between
   the title and the stat row so the reader sees "what is this?" before
   "how much is it?". Tightened margin-top (xs) so it visually attaches
   to the title rather than floating.

   `text-wrap: pretty` (Janne review v4) tells the browser to balance
   the last two lines so the description never ends on a one-word
   orphan — short tail words like "produced" or "society" used to wrap
   alone on the second line. Pretty is supported in all our target
   browsers (Chromium 117+, Safari 17.4+, Firefox 121+). */
const Definition = styled.div`
  margin-top: calc(var(--space__xs) * -1);
  font-size: var(--font-size__sm);
  color: var(--color__gray-700);
  line-height: 1.5;
  text-wrap: pretty;
`;

/* Stat row — three equal-width left-aligned columns separated by thin
   gray-200 dividers (Janne review v4). The dividers replace the gap-
   only spacing from v3, which read as "three floating labels" without
   any structural cue tying them together. Left-aligning the values
   keeps the eye anchored to the same vertical axis as the title and
   definition above. */
const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: var(--space__sm);
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding-right: var(--space__md);

  & + & {
    padding-left: var(--space__md);
    border-left: 1px solid var(--color__gray-200);
  }
`;

const StatLabel = styled.span`
  font-size: var(--font-size__xs);
  letter-spacing: 0.04em;
  font-weight: var(--font-weight__medium);
  color: var(--color__gray-600);
  text-transform: uppercase;
`;

const StatValue = styled.span<{ $valence: Valence }>`
  font-size: var(--font-size__xl);
  font-weight: var(--font-weight__bold);
  color: ${p =>
    p.$valence === Valence.POSITIVE
      ? 'var(--color__teal-700)'
      : p.$valence === Valence.NEGATIVE
        ? 'var(--color__purple-700)'
        : 'var(--color__gray-900)'};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
`;

/* Stacks the contributing-products list and the (optional) Leena-2 "Top impacts"
   list within a valence tab, with clear separation between the two sections. */
const ValenceTabBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__2xl);
`;

const TabsWrap = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  /* The scroll now lives on \`ScrollArea\` below, not here — the tab strip is
     pinned above it. Keep this container clip-only so nothing spills past the
     panel's rounded corners. */
  overflow: hidden;

  /* react-aria-components \`Tabs\` renders its own wrapper <div> around the
     TabList + TabPanels. Make that wrapper the flex column that fills the
     panel height, so \`TabStrip\` can stay pinned (flex-shrink: 0) while
     \`ScrollArea\` takes the remaining height and scrolls. It's the only
     direct child of \`TabsWrap\`. */
  & > div {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
`;

/* Pinned tab strip. Sits above the scroll area (Janne review, Aug 2026): the
   tabs stay put while the body scrolls under them. No divider line — the
   separation reads from the tab shapes themselves plus the gap to the content
   below, which Janne preferred over an explicit border rule. */
const TabStrip = styled.div`
  flex-shrink: 0;
  /* No bottom padding: the scroll boundary sits flush at the tabs' lower edge,
     so the space below the tabs lives inside the scroll area (its top padding)
     and scrolls with the content rather than reading as a fixed dead band under
     the strip (Janne review, Aug 2026). */
  padding: var(--space__md) var(--space__xl) 0;

  /* Stretch the tab strip to the full panel width and let each tab grow to
     fill an equal share, so the strip reads as a full-width bar regardless of
     tab count — a 3-tab lens (e.g. Knowledge) spans edge-to-edge the same as
     the 5-tab one, rather than shrinking to a small centred pill (Janne
     review, Aug 2026).

     IMPORTANT: react-aria-components \`Tabs\` wraps its children in a <div>,
     so the \`TabList\` is a descendant, not a direct child — the descendant
     selector below is required for these overrides to apply. */
  [role='tablist'] {
    /* Full panel width; tabs below grow to fill it. */
    width: 100%;
    /* Small fixed gap between tabs; the equal-growth below distributes the
       remaining width, so this only sets the minimum separation. */
    gap: var(--space__sm);
    /* No bottom margin: the scroll area's top padding provides the separation
       from the content now. */
    margin: 0;
  }

  [role='tablist'] > [role='tab'] {
    /* Each tab grows to fill an equal share of the full-width strip
       (\`flex-grow: 1\`), and \`flex-shrink: 1\` + \`min-width: 0\` let them
       give width back if the labels would otherwise exceed the panel's
       minimum width, so nothing overflows past the right edge (five tabs
       used to clip "Chat"). */
    flex: 1 1 auto;
    min-width: 0;
    justify-content: center;
    /* Bump the vertical hit-area so the tab strip reads as a proper nav
       element rather than a thin pill (Janne review v4 said it was almost
       invisible). */
    padding-top: var(--space__sm);
    padding-bottom: var(--space__sm);
  }
`;

/* Scrolling body below the pinned strip. Owns the vertical scroll and the tab
   body's padding; its top padding is the breathing room between the strip's
   border and the content. */
const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space__lg) var(--space__xl);

  [role='tabpanel'] {
    /* Let the Chat tab body fill the available height. */
    min-height: 100%;
    display: flex;
    flex-direction: column;
  }
`;
